import { useParams, useNavigate } from 'react-router-dom'
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from '@mui/material'
import { Description, Download, ArrowBack } from '@mui/icons-material'
import { useGetExamInfoQuery } from '../store/api/casesApi'
import { PageHeader } from '../components/PageHeader'
import { getContentPaperSx, getTablePaperSx } from '../theme/sectionSurfaces'
import { API_BASE_URL } from '../config/api'

/**
 * UAT-TC #7: Đề thi đầy đủ — tóm tắt + danh mục tài liệu + yêu cầu bài nộp.
 */
export default function ExamInfoPage() {
  const { caseId = '' } = useParams<{ caseId: string }>()
  const navigate = useNavigate()
  const { data, isLoading, error } = useGetExamInfoQuery(caseId, { skip: !caseId })

  const handleDownloadExamPaper = () => {
    const token = localStorage.getItem('access_token') || ''
    const url = `${API_BASE_URL}/cases/${caseId}/exam-paper`
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = u
        a.download = `exam-paper-${caseId}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(u)
      })
      .catch(() => alert('Không tải được đề thi'))
  }

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3, display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Container>
    )
  }

  if (error || !data) {
    return (
      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Alert severity="error">
          Không thể tải đề thi. Có thể đội bạn chưa được duyệt hoặc cuộc thi chưa cấp đề.
        </Alert>
        <Button startIcon={<ArrowBack />} sx={{ mt: 2 }} onClick={() => navigate('/cases')}>
          Quay về danh sách cuộc thi
        </Button>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 } }}>
      <PageHeader
        title={`Đề thi: ${data.title}`}
        subtitle={data.case_number ? `Số: ${data.case_number}` : undefined}
        icon={<Description />}
        actions={
          <Button startIcon={<ArrowBack />} variant="outlined" onClick={() => navigate('/cases')}>
            Quay lại
          </Button>
        }
      />

      {/* Section 1: Tóm tắt vụ án */}
      <Paper elevation={0} sx={(t) => ({ ...getContentPaperSx(t), mb: 3 })}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          1. Tóm tắt vụ án
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {data.case_type && <Chip label={`Loại: ${data.case_type}`} size="small" />}
          {data.legal_area && <Chip label={`Lĩnh vực: ${data.legal_area}`} size="small" />}
          {data.dispute_value && <Chip label={`Giá trị: ${data.dispute_value}`} size="small" color="warning" />}
        </Box>
        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
          {data.summary || 'Chưa có tóm tắt.'}
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Nguyên đơn</Typography>
            <Typography variant="body2" fontWeight={500}>{data.plaintiff || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Bị đơn</Typography>
            <Typography variant="body2" fontWeight={500}>{data.defendant || '—'}</Typography>
          </Box>
        </Box>
        {data.parties_requests && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">Yêu cầu các bên</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {data.parties_requests}
            </Typography>
          </>
        )}
      </Paper>

      {/* Section 2: Danh mục tài liệu */}
      <Paper elevation={0} sx={(t) => ({ ...getContentPaperSx(t), mb: 3 })}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            2. Danh mục tài liệu
          </Typography>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownloadExamPaper}
            sx={{ textTransform: 'none' }}
          >
            Tải đề thi
          </Button>
        </Box>
        {data.materials_list && (
          <Alert severity="info" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
            {data.materials_list}
          </Alert>
        )}
        {data.materials.length > 0 ? (
          <TableContainer component={Paper} variant="outlined" sx={(t) => getTablePaperSx(t)}>
            <Table size="small" sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 220 }}><strong>Tài liệu</strong></TableCell>
                  <TableCell sx={{ minWidth: 120 }}><strong>Loại</strong></TableCell>
                  <TableCell sx={{ minWidth: 100 }}><strong>Định dạng</strong></TableCell>
                  <TableCell sx={{ minWidth: 100 }}><strong>Kích thước</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.materials.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell>{m.title || m.file_name}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={m.document_type}
                        color={m.document_type === 'case_document' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{(m.mime_type || '').split('/')[1] || m.mime_type}</TableCell>
                    <TableCell>{(m.file_size / 1024).toFixed(1)} KB</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="warning">
            Chưa có tài liệu đính kèm. Liên hệ BTC nếu thiếu.
          </Alert>
        )}
      </Paper>

      {/* Section 3: Yêu cầu bài nộp */}
      <Paper elevation={0} sx={(t) => getContentPaperSx(t)}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          3. Yêu cầu bài nộp
        </Typography>
        {data.submission_requirements ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {data.submission_requirements}
          </Typography>
        ) : (
          <Alert severity="info">
            BTC chưa cập nhật yêu cầu bài nộp. Vui lòng theo dõi thông báo.
          </Alert>
        )}
      </Paper>
    </Container>
  )
}
